import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { SITE } from "@/lib/site";
import { ChevronLeft, Lock, Mail, Eye, EyeOff, Loader2 } from "lucide-react";
import PageSeo from "@/components/PageSeo";

/**
 * Staff Portal sign-in.
 *
 * Standalone full-screen route — the public site header/footer are deliberately
 * absent so this feels like a private back-office console, not a page inside the
 * marketing site. Responsive: split-panel on desktop, stacked on mobile.
 */
export default function Login() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/staff";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, pw);
      toast.success("Signed in");
      nav(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Login failed. Check credentials.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div data-testid="login-page" className="min-h-svh w-full bg-[hsl(var(--bg))] text-[hsl(var(--ink))]">
      <PageSeo
        title="Staff Sign in — The Barracks Bistro"
        description="Staff portal for The Barracks Bistro back office."
        path="/login"
      />

      <div className="min-h-svh grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
        {/* -------- Editorial brand panel (desktop only, banner on mobile) -------- */}
        <aside
          data-testid="login-brand-panel"
          className="relative overflow-hidden bg-[hsl(var(--forest))] text-[hsl(var(--bg))] px-6 py-10 sm:px-10 lg:px-16 lg:py-16 flex flex-col justify-between min-h-[38svh] lg:min-h-svh"
        >
          {/* Subtle brass-rule frame */}
          <div className="absolute inset-4 border border-[hsl(var(--brass))]/25 pointer-events-none hidden sm:block" aria-hidden />
          {/* Grain overlay */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay"
            style={{
              backgroundImage:
                "radial-gradient(hsl(var(--bg)) 1px, transparent 1px)",
              backgroundSize: "3px 3px",
            }}
          />

          <div className="relative z-10">
            <Link to="/" data-testid="login-back-to-site" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--brass))]/85 hover:text-[hsl(var(--brass))] transition-colors">
              <ChevronLeft size={12} /> Back to public site
            </Link>
            <div className="mt-8 flex items-baseline gap-2 font-serif">
              <span className="text-3xl sm:text-4xl italic tracking-tight">TBB</span>
              <span className="text-xs sm:text-sm uppercase tracking-[0.32em] text-[hsl(var(--brass))]">Back Office</span>
            </div>
          </div>

          <div className="relative z-10 max-w-md">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[hsl(var(--brass))]/85">Private portal</div>
            <h1 className="mt-4 display leading-tight text-3xl sm:text-4xl lg:text-[2.75rem]">
              Sign in to run<br />
              <span className="italic text-[hsl(var(--brass))]">The Barracks Bistro</span>.
            </h1>
            <div className="mt-5 h-px w-24 bg-[hsl(var(--brass))]" />
            <p className="mt-5 text-sm leading-relaxed text-[hsl(var(--bg))]/75">
              Reservations, procurement, item master, GRNs, payouts, staff meals, kitchen briefs —
              every touchpoint from guest to plate, ledgered.
            </p>

            {/* Highlight strip — feels like a hotel back-of-house wall notice */}
            <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--bg))]/70">
              <li>· Reservations</li>
              <li>· Procurement</li>
              <li>· GRNs &amp; Payouts</li>
              <li>· Item Master</li>
              <li>· Indents</li>
              <li>· Vendor Ledgers</li>
            </ul>
          </div>

          <div className="relative z-10 text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--bg))]/50 mt-8">
            © {year} · EEPL · Est. 1998
          </div>
        </aside>

        {/* -------- Sign-in card panel -------- */}
        <section
          data-testid="login-form-panel"
          className="flex items-center justify-center px-5 py-10 sm:px-10 lg:px-16"
        >
          <form
            data-testid="login-form"
            onSubmit={submit}
            noValidate
            className="w-full max-w-md"
          >
            <div className="eyebrow">Sign in</div>
            <h2 className="mt-2 display text-2xl sm:text-3xl">Welcome back.</h2>
            <p className="mt-2 text-sm text-[hsl(var(--ink-soft))]">
              Use the credentials issued by your manager. All actions here are audit-logged.
            </p>

            <div className="mt-8 space-y-5">
              {/* Email */}
              <label className="block">
                <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-[hsl(var(--ink-soft))] mb-2">
                  Email address
                </span>
                <div className="relative">
                  <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ink-soft))] pointer-events-none" />
                  <input
                    data-testid="login-email-input"
                    type="email"
                    autoComplete="username"
                    inputMode="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-[hsl(var(--bg-2))] border border-[hsl(var(--rule))] rounded-md pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[hsl(var(--forest))] focus:ring-2 focus:ring-[hsl(var(--forest))]/15 transition-shadow"
                    placeholder="you@thebarracksbistro.in"
                  />
                </div>
              </label>

              {/* Password */}
              <label className="block">
                <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-[hsl(var(--ink-soft))] mb-2">
                  Password
                </span>
                <div className="relative">
                  <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ink-soft))] pointer-events-none" />
                  <input
                    data-testid="login-password-input"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    className="w-full bg-[hsl(var(--bg-2))] border border-[hsl(var(--rule))] rounded-md pl-10 pr-11 py-3 text-sm focus:outline-none focus:border-[hsl(var(--forest))] focus:ring-2 focus:ring-[hsl(var(--forest))]/15 transition-shadow"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    data-testid="login-toggle-password"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]"
                  >
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </label>

              <button
                data-testid="login-submit"
                type="submit"
                disabled={busy}
                className="btn-brass w-full justify-center py-3 text-sm inline-flex items-center gap-2 disabled:opacity-60"
              >
                {busy ? (<><Loader2 size={14} className="animate-spin" /> Signing in…</>) : "Sign in"}
              </button>

              <p className="text-[11px] text-[hsl(var(--ink-soft))] leading-relaxed text-center">
                Forgot your password?{" "}
                <a
                  href={SITE.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="login-whatsapp-help"
                  className="text-[hsl(var(--brass-2))] hover:underline"
                >
                  WhatsApp the manager
                </a>{" "}
                or email{" "}
                <a href={`mailto:${SITE.email}`} data-testid="login-email" className="text-[hsl(var(--brass-2))] hover:underline">
                  {SITE.email}
                </a>.
              </p>
            </div>

            <div className="mt-10 pt-6 border-t border-[hsl(var(--rule))] text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--ink-soft))]/70 flex items-center justify-between">
              <span>Est. 1998 · Wellington</span>
              <Link to="/" className="hover:text-[hsl(var(--brass-2))]">Public site →</Link>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
